import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useShopFile } from '../../components/ShopFileLayout';
import { PageBody } from '../../components/Page';
import { Badge } from '../../components/Pill';
import { StatusBanner } from '../../components/StatusBanner';
import { intlLocale } from '../../i18n/formats';
import { useLocale } from '../../i18n/LocaleProvider';
import type { SupportedLocale } from '../../i18n';
import { isShopAccessRequired, opsErrorMessage } from '../../lib/ops-api';
import {
  MAX_TRIAL_EXTENSION_DAYS,
  formatDiscount,
  formatMoney,
  parseTrialDays,
  previewTrialEnd,
  trialBlockerKey,
  validateTrialExtension,
  type SubscriptionSyncOutcome,
} from '../../lib/ops-billing';
import {
  extendShopTrial,
  fetchShopBilling,
  syncShopSubscription,
  type OpsBilling,
  type TrialExtensionResult,
} from '../../lib/ops-billing-api';
import { isReasonAcceptable, REASON_MIN_LENGTH } from '../../lib/shop-file';

/** A date-time as the reader's locale writes it, or a dash. */
function when(
  value: string | null | undefined,
  locale: SupportedLocale,
): string {
  if (!value) return '—';
  return new Date(value).toLocaleString(intlLocale(locale), {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/** A date as the reader's locale writes it, or a dash. */
function day(
  value: string | null | undefined,
  locale: SupportedLocale,
): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(intlLocale(locale), {
    dateStyle: 'medium',
  });
}

/** One labelled fact in a definition grid. */
function Fact({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-gray-500">{label}</dt>
      <dd className="text-sm text-gray-900 break-words">{children}</dd>
    </div>
  );
}

/** A card with a heading and an optional lead. */
function Section({
  title,
  lead,
  children,
}: {
  readonly title: string;
  readonly lead?: string;
  readonly children: ReactNode;
}) {
  return (
    <section className="card space-y-3">
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      {lead && <p className="text-sm text-gray-600">{lead}</p>}
      {children}
    </section>
  );
}

/** A reason textarea with the shared floor and a counter. */
function ReasonField({
  id,
  value,
  onChange,
  placeholder,
}: {
  readonly id: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder: string;
}) {
  const { t } = useLocale();
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="field-label">
        {t('opsBilling.reasonLabel')}
      </label>
      <textarea
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        rows={2}
        maxLength={500}
        placeholder={placeholder}
        className="field-input"
      />
      <div className="flex justify-between gap-3">
        <p className="field-hint">
          {t('opsBilling.reasonHint', { min: REASON_MIN_LENGTH })}
        </p>
        <p className="field-hint tabular-nums shrink-0">{value.length}/500</p>
      </div>
    </div>
  );
}

/**
 * The shop file's billing tab (work order item 7): everything behind "why was I charged / why is
 * my plan wrong", plus the two billing actions support may take — a sync from Shopify (AC-7.1)
 * and a trial extension (AC-7.3).
 *
 * Both actions are audited with a typed reason and go through a confirm step (G4). Neither
 * changes what the merchant has agreed to pay: the sync copies Shopify's state onto ours, and the
 * extension uses Shopify's own trial mechanism, so the merchant sees it in their Shopify admin.
 */
export function BillingTab() {
  const { t, tCount, locale } = useLocale();
  const { shopId, file, requireAccess } = useShopFile();

  const billing = useQuery({
    queryKey: ['shop-billing', shopId],
    queryFn: () => fetchShopBilling(shopId),
    retry: false,
  });

  // A grant that expired mid-visit: hand the file back to the reason prompt. In an effect, since
  // it updates the parent layout's state.
  const accessRefused =
    billing.isError && isShopAccessRequired(billing.error, shopId);
  useEffect(() => {
    if (accessRefused) requireAccess();
  }, [accessRefused, requireAccess]);

  if (accessRefused) return null;

  if (billing.isError) {
    return (
      <PageBody>
        <StatusBanner tone="error">
          {opsErrorMessage(billing.error, t('opsBilling.loadFailed'))}
        </StatusBanner>
      </PageBody>
    );
  }

  if (!billing.data) {
    return (
      <PageBody>
        <StatusBanner tone="loading">{t('common.loading')}</StatusBanner>
      </PageBody>
    );
  }

  const data = billing.data;
  const currency = data.billingCurrency;
  const intl = intlLocale(locale);

  return (
    <PageBody>
      <StatusBanner tone="info">
        {t('opsBilling.currencyNote', { currency })}
      </StatusBanner>

      <div className="grid gap-5 lg:grid-cols-2">
        <SubscriptionSection billing={data} />
        <SyncSection shopDomain={file.identity.shopDomain} />
      </div>

      <TrialSection billing={data} />

      <Section
        title={t('opsBilling.cycles.title')}
        lead={t('opsBilling.cycles.lead')}
      >
        {data.billingCycles.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('opsBilling.cycles.period')}</th>
                  <th>{t('opsBilling.cycles.kind')}</th>
                  <th>{t('opsBilling.cycles.invoices')}</th>
                  <th>{t('opsBilling.cycles.overage')}</th>
                  <th>{t('opsBilling.cycles.charged')}</th>
                </tr>
              </thead>
              <tbody>
                {data.billingCycles.map((cycle) => (
                  <tr key={cycle.id}>
                    <td className="whitespace-nowrap">
                      {day(cycle.periodStart, locale)} –{' '}
                      {day(cycle.periodEnd, locale)}
                    </td>
                    <td>
                      <Badge tone={cycle.isTrial ? 'sky' : 'gray'}>
                        {cycle.isTrial
                          ? t('opsBilling.cycles.trial')
                          : t('opsBilling.cycles.paid')}
                      </Badge>
                    </td>
                    <td className="tabular-nums">
                      {cycle.invoiceLimit < 0
                        ? t('opsBilling.cycles.countUnlimited', {
                            count: cycle.invoiceCount,
                          })
                        : t('opsBilling.cycles.countOfLimit', {
                            count: cycle.invoiceCount,
                            limit: cycle.invoiceLimit,
                          })}
                    </td>
                    <td className="tabular-nums">{cycle.overageCount}</td>
                    <td className="tabular-nums">
                      {formatMoney(cycle.overageCharged, currency, intl)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-600">{t('opsBilling.cycles.none')}</p>
        )}
      </Section>

      <Section
        title={t('opsBilling.failedUsage.title')}
        lead={t('opsBilling.failedUsage.lead')}
      >
        {data.failedUsageRecords.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('opsBilling.failedUsage.created')}</th>
                  <th>{t('opsBilling.failedUsage.amount')}</th>
                  <th>{t('opsBilling.failedUsage.invoices')}</th>
                  <th>{t('opsBilling.failedUsage.retries')}</th>
                  <th>{t('opsBilling.failedUsage.state')}</th>
                  <th>{t('opsBilling.failedUsage.lastError')}</th>
                </tr>
              </thead>
              <tbody>
                {data.failedUsageRecords.map((record) => (
                  <tr key={record.id}>
                    <td className="whitespace-nowrap">
                      {when(record.createdAt, locale)}
                    </td>
                    <td className="tabular-nums">
                      {formatMoney(record.amount, record.currencyCode, intl)}
                    </td>
                    <td className="tabular-nums">
                      {record.chargeableInvoices}
                    </td>
                    <td className="tabular-nums">{record.retryCount}</td>
                    <td>
                      {record.resolvedAt ? (
                        <Badge tone="green">
                          {t('opsBilling.failedUsage.resolved', {
                            date: day(record.resolvedAt, locale),
                          })}
                        </Badge>
                      ) : (
                        <Badge tone="red">
                          {t('opsBilling.failedUsage.open')}
                        </Badge>
                      )}
                    </td>
                    <td className="text-xs text-red-700 max-w-md truncate">
                      {record.lastError ?? ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-600">
            {t('opsBilling.failedUsage.none')}
          </p>
        )}
      </Section>

      <div className="grid gap-5 lg:grid-cols-2">
        <Section
          title={t('opsBilling.vouchers.title')}
          lead={t('opsBilling.vouchers.lead')}
        >
          {data.voucherUsages.length > 0 ? (
            <ul className="space-y-2 text-sm">
              {data.voucherUsages.map((usage) => (
                <li
                  key={`${usage.voucherId}-${usage.usedAt}`}
                  className="flex flex-wrap items-center gap-2"
                >
                  <span className="font-mono">{usage.code}</span>
                  <span>
                    {formatDiscount(
                      usage.discountType,
                      usage.discountValue,
                      currency,
                      intl,
                    )}
                  </span>
                  <span className="text-gray-500">
                    {usage.durationLimitInIntervals === null ||
                    usage.durationLimitInIntervals === undefined
                      ? t('opsBilling.vouchers.durationUnlimited')
                      : tCount(
                          'opsBilling.vouchers.duration',
                          usage.durationLimitInIntervals,
                        )}
                  </span>
                  <Badge tone={usage.onCurrentSubscription ? 'green' : 'gray'}>
                    {usage.onCurrentSubscription
                      ? t('opsBilling.vouchers.onCurrent')
                      : t('opsBilling.vouchers.notOnCurrent')}
                  </Badge>
                  <span className="text-gray-500">
                    {day(usage.usedAt, locale)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-600">
              {t('opsBilling.vouchers.none')}
            </p>
          )}
          <Link to="/vouchers" className="btn-secondary btn-sm">
            {t('opsBilling.vouchers.manage')}
          </Link>
        </Section>

        <Section
          title={t('opsBilling.trialHistory.title')}
          lead={t('opsBilling.trialHistory.lead')}
        >
          {data.trialHistory ? (
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Fact label={t('opsBilling.trialHistory.first')}>
                {day(data.trialHistory.firstTrialStartedAt, locale)}
              </Fact>
              <Fact label={t('opsBilling.trialHistory.last')}>
                {day(data.trialHistory.lastTrialStartedAt, locale)}
              </Fact>
              <Fact label={t('opsBilling.trialHistory.count')}>
                {data.trialHistory.trialCount}
              </Fact>
            </dl>
          ) : (
            <p className="text-sm text-gray-600">
              {t('opsBilling.trialHistory.none')}
            </p>
          )}
        </Section>
      </div>

      <Section
        title={t('opsBilling.transitions.title')}
        lead={t('opsBilling.transitions.lead')}
      >
        {data.transitions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('opsBilling.transitions.when')}</th>
                  <th>{t('opsBilling.transitions.op')}</th>
                  <th>{t('opsBilling.transitions.reason')}</th>
                  <th>{t('opsBilling.transitions.subscription')}</th>
                </tr>
              </thead>
              <tbody>
                {data.transitions.map((row) => (
                  <tr key={row.id}>
                    <td className="whitespace-nowrap">
                      {when(row.createdAt, locale)}
                    </td>
                    <td>
                      <Badge
                        tone={
                          row.op === 'error'
                            ? 'red'
                            : row.op === 'none'
                              ? 'gray'
                              : 'amber'
                        }
                      >
                        {row.op}
                      </Badge>
                    </td>
                    <td className="text-xs font-mono max-w-md break-words">
                      {row.reason}
                    </td>
                    <td className="text-xs font-mono">
                      {row.subscriptionId ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-600">
            {t('opsBilling.transitions.none')}
          </p>
        )}
      </Section>

      <Section title={t('opsBilling.actions.title')}>
        {data.operatorActions.length > 0 ? (
          <ul className="space-y-1 text-sm">
            {data.operatorActions.map((action) => (
              <li key={action.auditLogId} className="flex flex-wrap gap-2">
                <span className="text-gray-500 whitespace-nowrap">
                  {when(action.createdAt, locale)}
                </span>
                <span className="font-mono text-xs">{action.action}</span>
                <span>{action.operator}</span>
                <span className="text-gray-600">{action.reason}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-600">
            {t('opsBilling.actions.none')}
          </p>
        )}
      </Section>
    </PageBody>
  );
}

/** The local subscription row, both slots, and what its tier costs. */
function SubscriptionSection({ billing }: { readonly billing: OpsBilling }) {
  const { t, locale } = useLocale();
  const sub = billing.subscription;
  const intl = intlLocale(locale);
  const currency = billing.billingCurrency;

  return (
    <Section title={t('opsBilling.subscription.title')}>
      {sub ? (
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Fact label={t('opsBilling.subscription.tier')}>{sub.tier}</Fact>
          <Fact label={t('opsBilling.subscription.status')}>
            <Badge tone={sub.status === 'ACTIVE' ? 'green' : 'amber'}>
              {sub.status}
            </Badge>{' '}
            {sub.bypass && (
              <Badge tone="sky">{t('opsBilling.subscription.bypass')}</Badge>
            )}
          </Fact>
          <Fact label={t('opsBilling.subscription.shopifyId')}>
            <span className="font-mono text-xs">{sub.subscriptionId}</span>
          </Fact>
          <Fact label={t('opsBilling.subscription.period')}>
            {day(sub.currentPeriodStart, locale)} –{' '}
            {day(sub.currentPeriodEnd, locale)}
          </Fact>
          <Fact label={t('opsBilling.subscription.basePrice')}>
            {formatMoney(sub.basePrice, currency, intl)}
          </Fact>
          <Fact label={t('opsBilling.subscription.invoiceLimit')}>
            {sub.invoiceLimit < 0
              ? t('opsBilling.subscription.unlimited')
              : sub.invoiceLimit}
          </Fact>
          <Fact label={t('opsBilling.subscription.overagePrice')}>
            {formatMoney(sub.overagePricePerInvoice, currency, intl)}
          </Fact>
          <Fact label={t('opsBilling.subscription.cap')}>
            {sub.cappedAmount === null || sub.cappedAmount === undefined
              ? t('opsBilling.subscription.capFromTier', {
                  amount: formatMoney(sub.tierCappedAmount, currency, intl),
                })
              : formatMoney(sub.cappedAmount, currency, intl)}
          </Fact>
          <Fact label={t('opsBilling.subscription.trial')}>
            {sub.isInTrial
              ? t('opsBilling.subscription.trialRunning', {
                  start: day(sub.trialStart, locale),
                  end: when(sub.trialEnd, locale),
                })
              : sub.trialEnd
                ? t('opsBilling.subscription.trialEnded', {
                    end: when(sub.trialEnd, locale),
                  })
                : t('opsBilling.subscription.noTrial')}
          </Fact>
          <Fact label={t('opsBilling.subscription.since')}>
            {when(sub.createdAt, locale)}
          </Fact>
          {sub.pendingSubscriptionId && (
            <div className="col-span-2 rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
              <p className="text-sm font-medium text-amber-900">
                {t('opsBilling.subscription.pendingTitle')}
              </p>
              <p className="text-xs text-amber-900">
                {t('opsBilling.subscription.pendingLead')}
              </p>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Fact label={t('opsBilling.subscription.tier')}>
                  {sub.pendingTier ?? '—'}
                </Fact>
                <Fact label={t('opsBilling.subscription.shopifyId')}>
                  <span className="font-mono text-xs">
                    {sub.pendingSubscriptionId}
                  </span>
                </Fact>
                <Fact label={t('opsBilling.subscription.cap')}>
                  {sub.pendingCappedAmount === null ||
                  sub.pendingCappedAmount === undefined
                    ? '—'
                    : formatMoney(sub.pendingCappedAmount, currency, intl)}
                </Fact>
                <Fact label={t('opsBilling.subscription.pendingSince')}>
                  {when(sub.pendingCreatedAt, locale)}
                </Fact>
                <Fact label={t('opsBilling.subscription.pendingUrl')}>
                  {sub.hasPendingConfirmationUrl
                    ? t('common.yes')
                    : t('common.no')}
                </Fact>
              </dl>
            </div>
          )}
        </dl>
      ) : (
        <p className="text-sm text-gray-600">
          {t('opsBilling.subscription.none')}
        </p>
      )}
    </Section>
  );
}

/**
 * The sync from Shopify (AC-7.1), for the file's shop and under its access grant. The domain is
 * only shown, in the confirmation, so the agent sees which shop is about to be rewritten.
 */
function SyncSection({ shopDomain }: { readonly shopDomain: string }) {
  const { t } = useLocale();
  const { shopId, reload, requireAccess } = useShopFile();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [outcome, setOutcome] = useState<SubscriptionSyncOutcome | null>(null);

  const sync = useMutation({
    mutationFn: () => syncShopSubscription(shopId, reason.trim()),
    onSuccess: (result) => {
      setOutcome(result);
      setReason('');
      void queryClient.invalidateQueries({
        queryKey: ['shop-billing', shopId],
      });
      reload();
    },
    onError: (error) => {
      if (isShopAccessRequired(error, shopId)) requireAccess();
    },
  });

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!isReasonAcceptable(reason)) return;
    setOutcome(null);
    setConfirming(true);
  }

  return (
    <Section
      title={t('opsBilling.sync.title')}
      lead={t('opsBilling.sync.lead')}
    >
      <form onSubmit={submit} className="space-y-3">
        <ReasonField
          id="billingSyncReason"
          value={reason}
          onChange={setReason}
          placeholder={t('opsBilling.sync.reasonPlaceholder')}
        />
        <button
          type="submit"
          className="btn-primary btn-sm"
          disabled={!isReasonAcceptable(reason) || sync.isPending}
        >
          {sync.isPending
            ? t('opsBilling.sync.running')
            : t('opsBilling.sync.submit')}
        </button>
      </form>

      {outcome && (
        <StatusBanner tone={outcome.synced ? 'success' : 'warning'}>
          <div className="space-y-1">
            <p>
              {outcome.synced
                ? t('opsBilling.sync.synced', {
                    status: outcome.localStatus ?? '—',
                    tier: outcome.localTier ?? '—',
                  })
                : t('opsBilling.sync.notSynced', {
                    reason: outcome.reason ?? '—',
                  })}
            </p>
            {outcome.repointedFrom && (
              <p>
                {t('opsBilling.sync.repointed', {
                  from: outcome.repointedFrom,
                })}
              </p>
            )}
            {outcome.auditLogId && (
              <p className="font-mono text-xs">
                {t('opsBilling.auditRow', { id: outcome.auditLogId })}
              </p>
            )}
          </div>
        </StatusBanner>
      )}
      {sync.isError && !isShopAccessRequired(sync.error) && (
        <StatusBanner tone="error">
          {opsErrorMessage(sync.error, t('opsBilling.sync.failed'))}
        </StatusBanner>
      )}

      <ConfirmDialog
        open={confirming}
        title={t('opsBilling.sync.confirmTitle')}
        confirmLabel={t('opsBilling.sync.confirm')}
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          sync.mutate();
        }}
      >
        <div className="space-y-2">
          <p>{t('opsBilling.sync.confirmBody', { shop: shopDomain })}</p>
          <p className="text-gray-500">{t('opsBilling.sync.confirmAudit')}</p>
        </div>
      </ConfirmDialog>
    </Section>
  );
}

/** The trial extension (AC-7.3): eligibility in plain words, then days + reason + confirm. */
function TrialSection({ billing }: { readonly billing: OpsBilling }) {
  const { t, tCount, locale } = useLocale();
  const { shopId, reload, requireAccess } = useShopFile();
  const queryClient = useQueryClient();
  const [daysInput, setDaysInput] = useState('7');
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<TrialExtensionResult | null>(null);

  const eligibility = billing.trialExtension;
  const currentEnd = billing.subscription?.trialEnd ?? null;
  const days = parseTrialDays(daysInput);
  const errors = validateTrialExtension(daysInput, reason);
  const canSubmit = eligibility.eligible && Object.keys(errors).length === 0;

  const extend = useMutation({
    mutationFn: (input: { days: number; reason: string }) =>
      extendShopTrial(shopId, input),
    onSuccess: (answer) => {
      setResult(answer);
      if (answer.extended) setReason('');
      void queryClient.invalidateQueries({
        queryKey: ['shop-billing', shopId],
      });
      reload();
    },
    onError: (error) => {
      if (isShopAccessRequired(error, shopId)) requireAccess();
    },
  });

  function submit(event: FormEvent): void {
    event.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    setResult(null);
    setConfirming(true);
  }

  return (
    <Section
      title={t('opsBilling.trial.title')}
      lead={t('opsBilling.trial.lead')}
    >
      {!eligibility.eligible && eligibility.blocker ? (
        <StatusBanner tone="info">
          {t(trialBlockerKey(eligibility.blocker))}
        </StatusBanner>
      ) : (
        <form onSubmit={submit} className="space-y-3 max-w-xl">
          <p className="text-sm text-gray-700">
            {t('opsBilling.trial.currentEnd', {
              end: when(currentEnd, locale),
            })}
          </p>
          <div className="space-y-1">
            <label htmlFor="trialDays" className="field-label">
              {t('opsBilling.trial.daysLabel')}
            </label>
            <input
              id="trialDays"
              type="number"
              inputMode="numeric"
              min={1}
              max={MAX_TRIAL_EXTENSION_DAYS}
              step={1}
              value={daysInput}
              onChange={(event) => setDaysInput(event.target.value)}
              className="field-input w-32"
            />
            {touched && errors.days ? (
              <p className="field-error">
                {t(errors.days, { max: MAX_TRIAL_EXTENSION_DAYS })}
              </p>
            ) : (
              <p className="field-hint">
                {t('opsBilling.trial.daysHint', {
                  max: MAX_TRIAL_EXTENSION_DAYS,
                })}
              </p>
            )}
            {days !== null && currentEnd && (
              <p className="field-hint">
                {t('opsBilling.trial.preview', {
                  end: when(previewTrialEnd(currentEnd, days), locale),
                })}
              </p>
            )}
          </div>
          <ReasonField
            id="trialReason"
            value={reason}
            onChange={setReason}
            placeholder={t('opsBilling.trial.reasonPlaceholder')}
          />
          <button
            type="submit"
            className="btn-primary btn-sm"
            disabled={!canSubmit || extend.isPending}
          >
            {extend.isPending
              ? t('opsBilling.trial.running')
              : t('opsBilling.trial.submit')}
          </button>
        </form>
      )}

      {result && result.extended && (
        <StatusBanner tone={result.resyncError ? 'warning' : 'success'}>
          <div className="space-y-1">
            <p>
              {t('opsBilling.trial.extended', {
                end: when(result.trialEndAfter, locale),
              })}
            </p>
            {result.trialDaysTotal !== null &&
              result.trialDaysTotal !== undefined && (
                <p>
                  {tCount('opsBilling.trial.totalDays', result.trialDaysTotal)}
                </p>
              )}
            {result.resyncError && (
              <p>
                {t('opsBilling.trial.resyncFailed', {
                  error: result.resyncError,
                })}
              </p>
            )}
            <p className="font-mono text-xs">
              {t('opsBilling.auditRow', { id: result.auditLogId })}
            </p>
          </div>
        </StatusBanner>
      )}
      {result && !result.extended && (
        <StatusBanner tone="warning">
          <div className="space-y-1">
            <p>{t('opsBilling.trial.refused')}</p>
            <ul className="list-disc pl-5">
              {result.userErrors.map((error, index) => (
                <li key={`${error.code ?? 'error'}-${index}`}>
                  {error.code ? (
                    <span className="font-mono">{error.code}</span>
                  ) : null}
                  {error.code ? ' — ' : ''}
                  {error.message}
                </li>
              ))}
            </ul>
            <p className="font-mono text-xs">
              {t('opsBilling.auditRow', { id: result.auditLogId })}
            </p>
          </div>
        </StatusBanner>
      )}
      {extend.isError && !isShopAccessRequired(extend.error) && (
        <StatusBanner tone="error">
          {opsErrorMessage(extend.error, t('opsBilling.trial.failed'))}
        </StatusBanner>
      )}

      <ConfirmDialog
        open={confirming && days !== null}
        title={t('opsBilling.trial.confirmTitle')}
        confirmLabel={t('opsBilling.trial.confirm')}
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          if (days !== null) extend.mutate({ days, reason: reason.trim() });
        }}
      >
        <div className="space-y-2">
          <p>
            {tCount('opsBilling.trial.confirmBody', days ?? 0, {
              from: when(currentEnd, locale),
              to:
                currentEnd && days !== null
                  ? when(previewTrialEnd(currentEnd, days), locale)
                  : '—',
            })}
          </p>
          <p>{t('opsBilling.trial.confirmShopify')}</p>
          <p>{t('opsBilling.trial.confirmUnlimited')}</p>
          <p className="text-gray-500">{t('opsBilling.trial.confirmAudit')}</p>
        </div>
      </ConfirmDialog>
    </Section>
  );
}
